import { observer } from 'mobx-react-lite'
import { useCallback, useEffect } from 'react'
import { useCountries } from 'Services/Cores/Countries/useCountries'
import { Initialize } from 'Services/Cores/Initialize'
import StoreCountry from './GlobalStores/Cores/County/StoreCountry'
import AppRouter from './Routers/AppRouter'
import mobiscroll from '@mobiscroll/react'
import BypassRedirectPath from './Data/BypassRedirectPath.json'
import StoreLoader from 'GlobalStores/Cores/Elements/StoreLoader'
import LoadingBar from 'Components/Elements/LoadingBar/LoadingBar'
import { isMaintenanceMode } from 'Helpers/GlobalHelpers'
import Maintenance from 'Components/Pages/Maintenance'
import TrackingEvents from 'Services/Tracker/TrackingEvents'
import ReactFbq from 'react-fbq'
import { get, lowerCase } from 'lodash'
import { Paths } from 'Routers/Paths'
import storeAuth from 'GlobalStores/User/StoreAuth'
import { useState } from 'react'
import StoreTranslate from 'GlobalStores/Cores/Translator/StoreTranslate'
import storeReCaptcha from 'GlobalStores/StoreReCaptcha'
import { autorun } from 'mobx'
import { useMemo } from 'react'
import { PathHelper } from 'GlobalStores/Cores/Helpers/PathHelper'
import feelGreateAPI from 'GlobalStores/Cores/County/APIs'
import SessionAuth from 'GlobalStores/User/SessionAuth'
import storeUser from 'GlobalStores/User/StoreUser'
import InAppSpy from 'inapp-spy'
import { FeelGreatAPI } from 'Services/FeelGreat/APIs'

mobiscroll.settings = {
    theme: 'ios',
    themeVariant: 'light'
}

const App = observer(() => {
    const [{ isInApp }] = useState(() => InAppSpy())
    const country = useCountries()
    const qs = useMemo(() => new URLSearchParams(window.location.search), [])

    const [autoLoginLoader, setAutoLoginLoader] = useState(false)

    const [goSelectCountry, setGoSelectCountry] = useState(false)

    Initialize()

    const GetSiteVersion = useCallback(() => {
        if (country) {
            feelGreateAPI.getSiteVersion({ country: country.country.toLowerCase() }).then(res => {
                let currentVersion = localStorage.getItem('siteVersion')
                if (!currentVersion) {
                    localStorage.setItem('siteVersion', res.data.version)
                } else if (currentVersion !== res.data.version) {
                    localStorage.clear()
                    sessionStorage.clear()
                    window.location.reload()
                }
            })
        }
    }, [country])

    useEffect(() => {
        if (isInApp) {
            openInBrowser()
        }
    }, [isInApp])

    const GetAutoLogin = async token => {
        try {
            setAutoLoginLoader(true)
            storeUser.reset()
            storeAuth.reset()
            SessionAuth.RemoveSession(true)

            setTimeout(async () => {
                await storeAuth.AutoLogin(token)
            }, 1000)
            checkToken()
            localStorage.removeItem('autoLogin')
            setAutoLoginLoader(false)
        } catch (e) {
            setAutoLoginLoader(false)
        }
    }

    function openInBrowser() {
        const url = window.location.href

        // Detect if the user is on Android or iOS
        const isAndroid = /Android/i.test(navigator.userAgent)
        const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent)

        // Get the current URL (to avoid infinite redirects)
        const currentUrl = window.location.href

        // Check if we are already on localhost or if it is the intent URL
        const isLocalhost = currentUrl.startsWith('http://localhost')
        const isIntentUrl = currentUrl.startsWith('intent://')

        if (!isLocalhost) {
            if (isAndroid && !isIntentUrl) {
                // Use the intent URL scheme for Android
                const intentUrl = 'intent://' + url.replace(/^https?:\/\//, '') + '#Intent;scheme=https;end'
                window.location.href = intentUrl
            } else if (isIOS) {
                // Open the URL in Safari on iOS
                window.open(url, '_system')
            } else {
                // Fallback for other platforms
                window.open(url, '_blank')
            }
        }
    }

    const checkFirstPathname = () => {
        if (StoreCountry.CountryList().length > 0) {
            const countryList = StoreCountry.CountryList()

            const index = countryList.findIndex(country => {
                const countryName = country.country.toLowerCase() || (country.country2 ? country.country2.toLowerCase() : '')
                return PathHelper.Segment(1) === countryName || BypassRedirectPath.includes(PathHelper.Segment(1))
            })

            return index > -1 ? true : false
        }
    }

    const checkToken = async () => {
        if (SessionAuth.GetLoginExtend('token')) {
            try {
                StoreLoader.SetApiLoaded('refreshToken', false)

                const response = await SessionAuth.CheckToken(
                    SessionAuth.GetLoginExtend('token'),
                    SessionAuth.GetLoginExtend('country'),
                    SessionAuth.GetLoginExtend('id')
                )
                if (response.data.token) {
                    SessionAuth.SetLoginExtend('token', response.data.token)
                }
                StoreLoader.SetApiLoaded('refreshToken', true)
            } catch (e) {
                SessionAuth.RemoveSession(true)
                StoreLoader.SetApiLoaded('refreshToken', true)
            }
        } else {
            StoreLoader.SetApiLoaded('refreshToken', true)
        }
    }

    useEffect(() => {
        if (country) {
            GetSiteVersion()
            const currentLanguageStorage = localStorage.getItem('currentLanguage')

            let countryLowerCase = country.country.replace(/ /g, '').toLowerCase()
            if (PathHelper.Segment(1) === 'saudi' || PathHelper.Segment(6) === 'saudi' || PathHelper.Segment(4) === 'saudi') {
                countryLowerCase = country.country2.replace(/ /g, '').toLowerCase()
            }
            const countryShortLowerCase = country.short.replace(/ /g, '').toLowerCase()

            if (currentLanguageStorage && currentLanguageStorage !== '' && hasLanguage(country, currentLanguageStorage)) {
                if (country.config.languageSettings.default !== currentLanguageStorage) {
                    localStorage.setItem('currentLanguage', country.config.languageSettings.default)
                    StoreTranslate.SetCurrentLanguage(country.config.languageSettings.default.replace('/ /g', ''))
                    storeReCaptcha.setCurrentLanguage(countryLowerCase, country.config.languageSettings.default.replace('/ /g', ''))
                } else {
                    StoreTranslate.SetCurrentLanguage(currentLanguageStorage)
                    storeReCaptcha.setCurrentLanguage(countryLowerCase, currentLanguageStorage)
                }
            } else {
                StoreTranslate.SetCurrentLanguage(country.config.languageSettings.default.replace('/ /g', ''))
                localStorage.setItem('currentLanguage', country.config.languageSettings.default.replace('/ /g', ''))
            }

            // set current country to local storage
            localStorage.setItem('currentCountry', countryLowerCase)
            if (localStorage.getItem('autoLogin')) {
                GetAutoLogin(localStorage.getItem('autoLogin'))
            } else {
                checkToken()
            }

            TrackingEvents.Commit(lowerCase(country.country))
            try {
                ReactFbq.initialize({
                    id: StoreCountry.FBPixelCode.all
                })
                ReactFbq.pageView()
            } catch (e) {
                console.error('FACEBOOK PIXEL ERROR', e)
            }

            if (PathHelper.Segment(1).toLowerCase() === countryShortLowerCase) {
                PathHelper.overriderCountryUrl({
                    countryName: countryLowerCase
                })
            } else if (PathHelper.Segments().length > 2 && isNaN(PathHelper.Segment(2)) && !isBypass()) {
                // * Case There are refCode (not number) or (Feature or Epic path)

                // Thinh told integrate with THA only [18JUN2024]
                if (PathHelper.Segment(1) === 'thailand' && PathHelper.Segment(2) === 'c') {
                    window.location.replace(window.location.pathname.replace('/c', ''))
                }

                if (Paths.includes(PathHelper.Segment(2))) {
                    window.location.replace(PathHelper.BuildBasename(countryLowerCase))
                }
            } else if (window.location.pathname === '/' || PathHelper.Segment(1) !== countryLowerCase) {
                window.location.replace(PathHelper.BuildBasename(countryLowerCase))
            }
        } else {
            StoreTranslate.SetCurrentLanguage('en')
        }
    }, [country, GetSiteVersion])

    /*
     * for redirecting reset password page
     */
    useEffect(() => {
        autorun(diposer => {
            if (StoreCountry.countryList) {
                if (qs.get('market') && /new-password/.test(window.location.pathname) && !!country === false) {
                    const marketCountry = StoreCountry.CountryList().find(item => item?.shorter?.toLowerCase() === lowerCase(qs.get('market')))
                    const isNewPasswordPath = PathHelper.Segment(1) === 'new-password'
                    const pathnames = window.location.pathname.split('/')
                    let newUrl = ''
                    if (marketCountry && isNewPasswordPath) {
                        if (/^\/feature/.test(window.location.pathname)) {
                            newUrl = `${window.location.protocol}//${window.location.host}/${pathnames[1]}/${pathnames[2]}/${
                                pathnames[3]
                            }/${marketCountry.country.toLowerCase()}/new-password/${pathnames[5]}/${window.location.search}`
                        } else if (/^\/epic/.test(window.location.pathname)) {
                            newUrl = `${window.location.protocol}//${window.location.host}/${pathnames[1]}/${pathnames[2]}/${pathnames[3]}/${pathnames[4]}/${
                                pathnames[5]
                            }/${marketCountry.country.toLowerCase()}/new-password/${pathnames[7]}/${window.location.search}`
                        } else {
                            newUrl = `${window.location.protocol}//${window.location.host}/${marketCountry.country.toLowerCase()}/new-password/${
                                pathnames[2]
                            }/${window.location.search}`
                        }
                        diposer.dispose()
                        window.location.href = newUrl
                    }
                } else {
                    const isRedirect = checkFirstPathname()

                    if (isRedirect !== undefined) {
                        if (isRedirect) {
                            return
                        } else {
                            window.location.replace(`/select-country`)
                        }
                    }

                    diposer.dispose()
                }
            }
        })

        if (/\/select-country/.test(window.location.pathname)) {
            setGoSelectCountry(true)
        }
    }, [])

    useEffect(() => {
        if (qs.get('autoLogin')) {
            localStorage.setItem('autoLogin', qs.get('autoLogin'))
            window.location.href = `${window.location.pathname}`
        }
    }, [qs])

    useEffect(() => {
        if (!storeAuth.IsAuthorized() && StoreCountry.Country3()) {
            checkGeoIPAccess()
            storeAuth.IsAllowAcceptLanguage()
        }
    }, [StoreCountry.Country3()])

    const checkGeoIPAccess = async () => {
        try {
            const { data } = await FeelGreatAPI.GetGeoIPAccessNoLogin({
                type: 'feelGreat',
                countryCode3Digit: StoreCountry.Country3()
            })

            if (!data.isPassAccess) {
                window.location.replace(`https://www.unicity.com`)
            }
        } catch (error) {
            console.error(error)
        }
    }

    return (
        <div className="App">
            {goSelectCountry ? (
                <AppRouter tiny={true} />
            ) : isMaintenanceMode() ? (
                <Maintenance />
            ) : (
                <>
                    <LoadingBar />
                    {StoreLoader.IsWebReady() && autoLoginLoader === false && <AppRouter />}
                </>
            )}
        </div>
    )
})

const isBypass = () => {
    const bypassPath = BypassRedirectPath

    return bypassPath.some(path => window.location.pathname.includes(path))
}

const hasLanguage = (country, currentLanguageStorage = '') => {
    const countryLanguageList = get(country, 'config.language.list', [])
    return countryLanguageList.some(lang => (lang.code || '').toLowerCase() === currentLanguageStorage.toLowerCase())
}

export default App
